import { Box, Paper } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { contactUsSendMessage } from 'api/contactUs/contactUs-api';
import { contactUsReasons } from 'features/constants';
import { useEffect, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { Notification } from 'ui-kit/components/notification/Notification';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import PhoneField from 'ui-kit/inputs/PhoneField/PhoneField';
import { contactUsSchema } from 'utilities/validation/schemas/contact-us-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import ContactUsBG from '../../common-assets/images/Contact-Us-Banner.jpg';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    imageContainer: {
      position: 'relative',
      width: '720px',
      height: '137px',
      backgroundColor: theme.palette.common.white,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    image: { minHeight: '100%', width: '100%', position: 'absolute', top: 0 },
    imageOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      backgroundColor: `${theme.palette.common.black}8C`,
      zIndex: 1,
    },
    imageText: {
      zIndex: 2,
      color: theme.palette.common.white,
      textAlign: 'center',
      maxWidth: '35ch',
    },
    mainContainer: {
      alignItems: 'center',
      background: theme.palette.common.white,
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
    },
    paper: {
      maxWidth: 720,
      overflow: 'hidden',
    },
  }),
);

const ContactUs = (): JSX.Element => {
  const classes = useStyles();
  const [contactUsObj, setContactUsObj] = useState<any>({
    contactUsFullName: '',
    contactUsFirm: '',
    contactUsEmail: '',
    contactUsPhone: '',
    contactUsReason: '',
    contactUsMessage: '',
  });
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [showNotificationSuccess, setShowNotificationSuccess] = useState<boolean>(false);
  const [showNotificationError, setShowNotificationError] = useState<boolean>(false);
  const handleSendMessage = async () => {
    setIsSubmitted(true);
    setShowNotificationSuccess(false);
    setShowNotificationError(false);
    if (!validationErrors || validationErrors.length === 0) {
      setIsSendingMessage(true);
      const requestObj = {
        fullName: contactUsObj.contactUsFullName,
        firm: contactUsObj.contactUsFirm,
        email: contactUsObj.contactUsEmail,
        phone: contactUsObj.contactUsPhone,
        reason: contactUsObj.contactUsReason,
        message: contactUsObj.contactUsMessage,
      };
      if (await contactUsSendMessage(requestObj)) {
        setIsSubmitted(false);
        setContactUsObj({
          contactUsFullName: '',
          contactUsFirm: '',
          contactUsEmail: '',
          contactUsPhone: '',
          contactUsReason: '',
          contactUsMessage: '',
        });
        setShowNotificationSuccess(true);
      } else {
        setShowNotificationError(true);
      }
      setIsSendingMessage(false);
    }
  };
  useEffect(() => {
    doValidate(contactUsObj, contactUsSchema(false)).then((errs: FieldValidationError[]) => {
      setValidationErrors(errs);
    });
  }, [contactUsObj]);

  return (
    <div className={classes.mainContainer}>
      <Paper className={classes.paper} elevation={2}>
        <Grid>
          <Grid item xs={12}>
            <div className={classes.imageContainer} id="b-6">
              <img className={classes.image} src={ContactUsBG} alt="contact-us" />
              <div className={classes.imageOverlay}></div>
              <Typography variant="h1" className={classes.imageText}>
                We&apos;re here to help!
              </Typography>
            </div>
          </Grid>
          <Box p={3}>
            <Box pb={2}>
              <Typography variant="h3">Contact Us</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <AgentNetTextInput
                  fullWidth
                  variant="outlined"
                  label="Full Name"
                  name="contactUsFullName"
                  id="contactUsFullName"
                  required
                  data-testid={'contactUsFullName'}
                  value={contactUsObj.contactUsFullName}
                  showValidation={isSubmitted}
                  onChange={(event: any) => setContactUsObj({ ...contactUsObj, contactUsFullName: event.target.value })}
                  errs={validationErrors}
                />
              </Grid>
              <Grid item xs={6}>
                <AgentNetTextInput
                  fullWidth
                  variant="outlined"
                  label="Firm"
                  name="contactUsFirm"
                  id="contactUsFirm"
                  required
                  data-testid={'contactUsFirm'}
                  value={contactUsObj.contactUsFirm}
                  showValidation={isSubmitted}
                  onChange={(event: any) => setContactUsObj({ ...contactUsObj, contactUsFirm: event.target.value })}
                  errs={validationErrors}
                />
              </Grid>

              <Grid item xs={6}>
                <AgentNetTextInput
                  fullWidth
                  variant="outlined"
                  label="Email"
                  name="contactUsEmail"
                  id="contactUsEmail"
                  required
                  data-testid={'contactUsEmail'}
                  value={contactUsObj.contactUsEmail}
                  showValidation={isSubmitted}
                  onChange={(event: any) => setContactUsObj({ ...contactUsObj, contactUsEmail: event.target.value })}
                  errs={validationErrors}
                />
              </Grid>
              <Grid item xs={6}>
                <PhoneField
                  fullWidth
                  variant="outlined"
                  label="Phone"
                  name="contactUsPhone"
                  id="contactUsPhone"
                  data-testid={'contactUsPhone'}
                  value={contactUsObj.contactUsPhone}
                  showValidation={isSubmitted}
                  onChange={(event: any) => setContactUsObj({ ...contactUsObj, contactUsPhone: event.target.value })}
                  errs={validationErrors}
                />
              </Grid>

              <Grid item xs={6}>
                <AgentNetDropdownSelector
                  name="contactUsReason"
                  required
                  showValidation={isSubmitted}
                  label="What can we help you with?"
                  options={contactUsReasons}
                  value={contactUsObj.contactUsReason}
                  id="contactUsReason"
                  menuOption={(val: string) => setContactUsObj({ ...contactUsObj, contactUsReason: val })}
                  qaAttribute="ContactUsReason"
                  dropdowntype="outlined"
                  errs={validationErrors}
                />
              </Grid>
              <Grid item xs={12}>
                <AgentNetTextInput
                  multiline
                  fullWidth
                  variant="outlined"
                  label="Message"
                  name="contactUsMessage"
                  id="contactUsMessage"
                  required
                  data-testid={'ContactUsMessage'}
                  value={contactUsObj.contactUsMessage}
                  showValidation={isSubmitted}
                  onChange={(event: any) => setContactUsObj({ ...contactUsObj, contactUsMessage: event.target.value })}
                  errs={validationErrors}
                />
              </Grid>
              <Grid item xs={12}>
                <AgentNetButton
                  color="primary"
                  size="small"
                  className="button"
                  variant="contained"
                  onClick={handleSendMessage}
                  loading={isSendingMessage}
                  disabled={isSendingMessage}
                >
                  Send Message
                </AgentNetButton>
              </Grid>
            </Grid>
            <div>
              {showNotificationError && (
                <Notification inline severity="error" msg="Unable to send message. Please try again." />
              )}
              {showNotificationSuccess && <Notification inline severity="success" msg="Your message has been sent." />}
            </div>
          </Box>
        </Grid>
      </Paper>
    </div>
  );
};
export default ContactUs;
